import { Package, Warehouse, Truck, CheckCircle, RotateCcw, Loader2 } from 'lucide-react';

const STATUS_CARDS = [
  {
    key: 'in_warehouse',
    label: 'En Bodega',
    icon: Warehouse,
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconColor: 'text-amber-500',
    countColor: 'text-amber-700',
    badgeBg: 'bg-amber-100',
  },
  {
    key: 'in_fastmax',
    label: 'En FastMax',
    icon: Package,
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconColor: 'text-blue-500',
    countColor: 'text-blue-700',
    badgeBg: 'bg-blue-100',
  },
  {
    key: 'with_driver',
    label: 'Con Conductor',
    icon: Truck,
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    iconColor: 'text-purple-500',
    countColor: 'text-purple-700',
    badgeBg: 'bg-purple-100',
  },
  {
    key: 'delivered',
    label: 'Entregado',
    icon: CheckCircle,
    bg: 'bg-green-50',
    border: 'border-green-200',
    iconColor: 'text-brand-green',
    countColor: 'text-green-700',
    badgeBg: 'bg-green-100',
  },
  {
    key: 'returned',
    label: 'Devuelto',
    icon: RotateCcw,
    bg: 'bg-red-50',
    border: 'border-red-200',
    iconColor: 'text-red-400',
    countColor: 'text-red-700',
    badgeBg: 'bg-red-100',
  },
];

/**
 * PackageStatusCards
 *
 * Renders 5 KPI cards, one per package status.
 * Each card is clickable and calls onStatusClick(statusKey)
 * which triggers a drill-down into the filtered package table.
 */
export function PackageStatusCards({ counts, total, loading, onStatusClick }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {STATUS_CARDS.map(card => (
          <div key={card.key} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
            <div className="h-8 bg-gray-100 rounded mb-2" />
            <div className="h-6 bg-gray-100 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Total count header */}
      <div className="flex items-center justify-between">
        <h2 className="font-montserrat font-bold text-gray-700 text-sm uppercase tracking-wide">
          Estado de Paquetes
        </h2>
        <span className="font-montserrat text-sm text-gray-500">
          {total ?? 0} total
        </span>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {STATUS_CARDS.map(card => {
          const Icon = card.icon;
          const count = counts?.[card.key] ?? 0;
          const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

          return (
            <button
              key={card.key}
              onClick={() => onStatusClick(card.key)}
              className={`
                ${card.bg} ${card.border}
                border rounded-xl p-4 text-left
                hover:shadow-md hover:scale-[1.02]
                active:scale-[0.98]
                transition-all duration-150 group
              `}
            >
              {/* Icon + count row */}
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg ${card.badgeBg} flex items-center justify-center`}>
                  <Icon size={20} className={card.iconColor} />
                </div>
                <span className={`font-montserrat font-extrabold text-2xl leading-none ${card.countColor}`}>
                  {count}
                </span>
              </div>

              {/* Label */}
              <p className="font-montserrat font-semibold text-gray-700 text-sm leading-tight mb-2">
                {card.label}
              </p>

              {/* Percentage bar */}
              <div className="w-full bg-white/60 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${card.badgeBg} ${card.iconColor} transition-all duration-500`}
                  style={{ width: `${percentage}%`, backgroundColor: 'currentColor', opacity: 0.6 }}
                />
              </div>
              <p className="text-xs text-gray-400 font-montserrat mt-1">
                {percentage}% del total
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}